/** Recent runs are bounded separately from today's savings so older pages cannot truncate the total. */
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  collection,
  getDocs,
  limit,
  orderBy,
  query,
  where
} from 'firebase/firestore';
import { db } from 'config/firebase';
import { useFirebaseAuth } from 'context/FirebaseAuthContext';
import { RUNS_COLLECTION } from '../config/rateShopConstants';
import { useRequestGuard, useRefreshOnVisible } from './useRequestGuard';

export function useRateShopRuns() {
  const [user, authLoading] = useFirebaseAuth();
  const [runs, setRuns] = useState([]);
  const [savingsToday, setSavingsToday] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const guard = useRequestGuard();
  const lastRefresh = useRef(0);
  const refresh = useCallback(async () => {
    if (!guard.mounted()) return;
    const token = guard.begin();
    if (authLoading || !user) {
      setRuns([]);
      setSavingsToday(0);
      setLoading(authLoading);
      setError(null);
      return;
    }
    lastRefresh.current = Date.now();
    setLoading(true);
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    const col = collection(db, RUNS_COLLECTION);
    try {
      const [recent, today] = await Promise.all([
        getDocs(query(col, orderBy('startedAt', 'desc'), limit(100))),
        getDocs(
          query(
            col,
            where('startedAt', '>=', start),
            orderBy('startedAt', 'desc')
          )
        )
      ]);
      if (!guard.current(token)) return;
      setRuns(recent.docs.map(d => ({ id: d.id, ...d.data() })));
      setSavingsToday(
        today.docs.reduce(
          (sum, d) => sum + (Number(d.data().projectedSavings) || 0),
          0
        )
      );
      setError(null);
    } catch (err) {
      if (guard.current(token)) setError(err);
    } finally {
      if (guard.current(token)) setLoading(false);
    }
  }, [authLoading, user, guard]);
  useEffect(() => {
    refresh();
    return guard.invalidate;
  }, [refresh, guard]);
  const refreshOnFocus = useCallback(() => {
    if (Date.now() - lastRefresh.current >= 120000) refresh();
  }, [refresh]);
  useRefreshOnVisible(refreshOnFocus);
  return {
    runs,
    latest: runs[0] || null,
    savingsToday,
    loading,
    error,
    refresh
  };
}
